import * as React from 'react';
import { HelpCircle, BookOpen, MessageCircleQuestion, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { type HelpContent } from '@/types/help-support';
import { cn } from '@/lib/utils';

interface HelpSupportDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: HelpContent;
}

export function HelpSupportDrawer({ open, onOpenChange, content }: HelpSupportDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="h-full w-full sm:!max-w-3xl">
        <DrawerHeader className="border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
              <HelpCircle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <DrawerTitle className="text-xl">{content.title}</DrawerTitle>
              <DrawerDescription>Help & Support</DrawerDescription>
            </div>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-6">
          <Tabs defaultValue="functionality" className="w-full py-6">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="functionality" className="gap-2">
                  <Lightbulb className="h-4 w-4" />
                  <span>Functionality</span>
                </TabsTrigger>
                <TabsTrigger value="guide" className="gap-2">
                  <BookOpen className="h-4 w-4" />
                  <span>User Guide</span>
                </TabsTrigger>
                <TabsTrigger value="faq" className="gap-2">
                  <MessageCircleQuestion className="h-4 w-4" />
                  <span>FAQ</span>
                </TabsTrigger>
              </TabsList>

            <TabsContent value="functionality" className="mt-6 space-y-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-foreground mb-2 text-lg font-semibold">Overview</h3>
                  <p className="text-muted-foreground leading-relaxed">{content.functionality.overview}</p>
                </div>

                <Separator />

                <div>
                  <h3 className="text-foreground mb-3 text-lg font-semibold">Key Features</h3>
                  <ul className="space-y-2">
                    {content.functionality.keyFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="bg-primary/10 text-primary mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                          {index + 1}
                        </div>
                        <span className="text-muted-foreground flex-1 leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="guide" className="mt-6 space-y-6">
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg border p-4">
                  <h3 className="text-foreground mb-2 flex items-center gap-2 font-semibold">
                    <Lightbulb className="text-primary h-5 w-5" />
                    Getting Started
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{content.userGuide.gettingStarted}</p>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="text-foreground text-lg font-semibold">Step-by-Step Guide</h3>
                  {content.userGuide.steps.map((step, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-start gap-3">
                        <Badge variant="outline" className="bg-primary/10 text-primary mt-0.5 shrink-0">
                          Step {index + 1}
                        </Badge>
                        <div className="flex-1 space-y-2">
                          <h4 className="text-foreground font-semibold">{step.title}</h4>
                          <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
                          {step.tips && step.tips.length > 0 && (
                            <div className="bg-blue-50 dark:bg-blue-950/20 mt-2 rounded-md border border-blue-200 p-3 dark:border-blue-900">
                              <p className="text-blue-900 dark:text-blue-100 mb-2 text-xs font-semibold uppercase tracking-wide">
                                💡 Tips
                              </p>
                              <ul className="space-y-1">
                                {step.tips.map((tip, tipIndex) => (
                                  <li key={tipIndex} className="text-blue-800 dark:text-blue-200 text-sm">
                                    • {tip}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                      {index < content.userGuide.steps.length - 1 && <Separator className="my-4" />}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="faq" className="mt-6 space-y-4">
              <div className="space-y-3">
                {content.faq.map((item, index) => (
                  <FAQItem key={index} question={item.question} answer={item.answer} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="border-t p-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm">
              Need more help?{' '}
              <a href="#" className="text-primary hover:underline">
                Contact Support
              </a>
            </p>
            <DrawerClose asChild>
              <Button variant="outline">Close</Button>
            </DrawerClose>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface FAQItemProps {
  question: string;
  answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors">
        <MessageCircleQuestion className="text-primary mt-0.5 h-5 w-5 shrink-0" />
        <div className="flex-1">
          <h4 className="text-foreground font-semibold">{question}</h4>
        </div>
        {isOpen ? (
          <ChevronUp className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
        ) : (
          <ChevronDown className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 pt-2">
        <div className="bg-muted/30 ml-8 rounded-md p-3">
          <p className="text-muted-foreground text-sm leading-relaxed">{answer}</p>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
